import { fonts } from "@theme";
import React from "react";
import LIcon from "./LIcon";
import LPressable from "./LPressable";
import LText from "./LText";
import {
  GoogleAuthProvider,
  OAuthCredential,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth } from "@utils/firebase.web";
import LBox from "@components/LBox";

interface ButtonProps {
  onComplete: (credential: OAuthCredential) => void;
  disabled?: boolean;
}

const Apple = ({ onComplete, disabled }: ButtonProps) => {
  async function onPress() {
    try {
      const provider = new OAuthProvider("apple.com");

      // Configure Apple provider with required scopes
      provider.addScope("email");
      provider.addScope("name");

      const result = await signInWithPopup(auth, provider);

      // The signed-in user info.
      console.log("Apple sign-in successful:", result.user.uid);

      // Apple credential
      const credential = OAuthProvider.credentialFromResult(result);

      if (credential) {
        onComplete(credential);
      } else {
        console.error("No credential returned from Apple sign-in");
      }
    } catch (error: any) {
      // Handle Errors here.
      const errorCode = error.code;
      const errorMessage = error.message;
      // The email of the user's account used.
      const email = error.customData?.email;
      // The credential that was used.
      const credential = OAuthProvider.credentialFromError(error);

      console.error("Apple sign-in error:", {
        errorCode,
        errorMessage,
        email,
        credential,
      });
    }
  }

  return (
    <LPressable
      sentryEventName="apple_login_pressed"
      onPress={onPress}
      disabled={disabled}
    >
      <LBox
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        gap="s3"
        backgroundColor="white"
        borderRadius="m"
        paddingVertical="s4"
        paddingHorizontal="s4"
        borderWidth={1}
        borderColor="greyBorder"
      >
        <LIcon colors={{ color: "#1C1C1C" }} name="apple" size={20} />
        <LText
          variant="bodyLBold"
          color="black"
          style={{ fontFamily: fonts.regular }}
        >
          Continue with Apple
        </LText>
      </LBox>
    </LPressable>
  );
};

// Google Sign-In is not available on web - use web-specific implementation
const Google = ({ onComplete, disabled }: ButtonProps) => {
  async function onPress() {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const credential = OAuthProvider.credentialFromResult(result);
      if (credential) {
        onComplete(credential);
      } else {
        console.error("No credential returned from Google sign-in");
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <LPressable
      sentryEventName="google_login_pressed"
      onPress={onPress}
      disabled={disabled}
    >
      <LBox
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        gap="s3"
        backgroundColor="white"
        borderRadius="m"
        paddingVertical="s4"
        paddingHorizontal="s4"
        borderWidth={1}
        borderColor="greyBorder"
      >
        <LIcon name="google" size={20} />
        <LText
          variant="bodyLBold"
          color="black"
          style={{ fontFamily: fonts.regular }}
        >
          Continue with Google
        </LText>
      </LBox>
    </LPressable>
  );
};

export { Apple, Google };

type SocialButtonProps = {
  type: "apple" | "google";
  onComplete?: (credential: OAuthCredential) => void;
  disabled?: boolean;
};

const SocialButton = ({ type, onComplete, disabled }: SocialButtonProps) => {
  const handleComplete = (credential: OAuthCredential) => {
    onComplete?.(credential);
  };

  switch (type) {
    case "apple":
      return <Apple onComplete={handleComplete} disabled={disabled} />;
    case "google":
      return <Google onComplete={handleComplete} disabled={disabled} />;
    default:
      return null;
  }
};
export default SocialButton;
